import jsPDF from 'jspdf';

/**
 * PDF Layout and Formatting Utilities
 */

// Font management - cache the base64 font data
let cachedFontBase64: string | null = null;

/**
 * Load custom font into PDF document
 *
 * jsPDF addFont signature: addFont(url, fontName, fontStyle, fontWeight, encoding)
 * We register the same font file with different styles so we can use:
 * - doc.setFont('Dax', 'normal') for regular text
 * - doc.setFont('Dax', 'bold') for bold text
 */
export const loadCustomFont = async (doc: jsPDF): Promise<void> => {
    try {
        // If we have cached font data, use it directly
        if (cachedFontBase64) {
            doc.addFileToVFS('Dax-Pro-Regular.ttf', cachedFontBase64);
            // Register same font file with different styles under same font family name
            doc.addFont('Dax-Pro-Regular.ttf', 'Dax', 'normal');
            doc.addFont('Dax-Pro-Regular.ttf', 'Dax', 'bold');
            doc.addFont('Dax-Pro-Regular.ttf', 'Dax', 'italic');
            doc.setFont('Dax', 'normal');
            return;
        }

        const fontUrl = '/Dax-Pro-Regular.ttf';
        const response = await fetch(fontUrl);

        if (!response.ok) {
            console.warn('Could not load custom font, using default');
            return;
        }

        const fontBlob = await response.blob();
        const reader = new FileReader();

        return new Promise((resolve) => {
            reader.onload = () => {
                try {
                    const base64Font = (reader.result as string).split(',')[1];

                    // Cache the font data for future use
                    cachedFontBase64 = base64Font;

                    doc.addFileToVFS('Dax-Pro-Regular.ttf', base64Font);
                    // Register same font file with different styles under same font family name
                    doc.addFont('Dax-Pro-Regular.ttf', 'Dax', 'normal');
                    doc.addFont('Dax-Pro-Regular.ttf', 'Dax', 'bold');
                    doc.addFont('Dax-Pro-Regular.ttf', 'Dax', 'italic');
                    doc.setFont('Dax', 'normal');

                    console.log('Custom font Dax loaded successfully');
                    resolve();
                } catch (error) {
                    console.warn('Error registering custom font:', error);
                    resolve();
                }
            };

            reader.onerror = () => {
                console.warn('Error reading font file');
                resolve();
            };

            reader.readAsDataURL(fontBlob);
        });
    } catch (error) {
        console.warn('Could not load custom font:', error);
    }
};

/**
 * Add footer with page numbers to PDF
 */
export const addFooter = (doc: jsPDF, pageNumber: number, totalPages: number): void => {
    const pageHeight = doc.internal.pageSize.height;

    doc.setFontSize(8);
    doc.setFont('Dax', 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(`Seite ${pageNumber} von ${totalPages}`, 170, pageHeight - 20);
};

/**
 * Add a section header with underline
 */
export const addSection = (doc: jsPDF, title: string, y: number): number => {
    doc.setFontSize(14);
    doc.setFont('Dax', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text(title, 20, y);
    doc.setLineWidth(0.5);
    doc.setDrawColor(0, 0, 0);
    doc.line(20, y + 2, 190, y + 2);
    // Reset to Dax font for subsequent text
    doc.setFont('Dax', 'normal');
    return y + 10;
};

/**
 * Add a key-value pair with proper formatting
 */
export const addKeyValue = (
    doc: jsPDF,
    key: string,
    value: string,
    x: number,
    y: number,
    maxWidth: number = 170
): number => {
    doc.setFont('Dax', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`${key}:`, x, y);

    doc.setFont('Dax', 'normal');
    doc.setTextColor(60, 60, 60);
    const keyWidth = doc.getTextWidth(`${key}: `);
    const minimumGap = 5;
    const actualKeyWidth = Math.max(keyWidth + minimumGap, 50);
    const lines = doc.splitTextToSize(value || '-', maxWidth - actualKeyWidth);
    doc.text(lines, x + actualKeyWidth, y);

    return y + (lines.length * 5);
};

/**
 * Check if page break is needed and add new page if necessary
 */
export const checkAndAddPageBreak = (
    doc: jsPDF,
    currentY: number,
    pageNumber: number,
    totalPages: number,
    continuationTitle: string = 'BILDER (Fortsetzung)',
    threshold: number = 120
): { y: number; pageNumber: number } => {
    const pageHeight = doc.internal.pageSize.height;

    if (currentY > pageHeight - threshold) {
        addFooter(doc, pageNumber, totalPages);
        doc.addPage();
        const newPageNumber = pageNumber + 1;
        let newY = 20;
        newY = addSection(doc, continuationTitle, newY);
        newY += 5;
        return {y: newY, pageNumber: newPageNumber};
    }

    return {y: currentY, pageNumber};
};

/**
 * Check if content fits on current page, add page break if needed (simple version without footer/section)
 * Returns the Y position to use (either current or top of new page)
 */
export const ensureSpace = (
    doc: jsPDF,
    currentY: number,
    requiredSpace: number,
    marginBottom: number = 30
): number => {
    const pageHeight = doc.internal.pageSize.height;

    if (currentY + requiredSpace > pageHeight - marginBottom) {
        doc.addPage();
        return 30; // Start position on new page
    }

    return currentY;
};

/**
 * Add signature section to PDF
 */
export const addSignatureSection = async (
    doc: jsPDF,
    signature: string,
    startY: number
): Promise<number> => {
    let currentY = startY;
    const pageHeight = doc.internal.pageSize.height;

    if (currentY > pageHeight - 60) {
        doc.addPage();
        currentY = 30;
    }
    currentY += 5;
    currentY = addSection(doc, 'Unterschrift', currentY);

    const signatureX = 20;
    const maxWidth = 80;
    const maxHeight = 25;

    await new Promise<void>((resolve) => {
        const signatureImg = new Image();
        signatureImg.onload = function () {
            try {
                const aspectRatio = signatureImg.width / signatureImg.height;

                let width = maxWidth;
                let height = maxWidth / aspectRatio;

                if (height > maxHeight) {
                    height = maxHeight;
                    width = maxHeight * aspectRatio;
                }

                doc.addImage(signature, 'PNG', signatureX, currentY, width, height);

                resolve();
            } catch (error) {
                resolve();
            }
        };

        signatureImg.onerror = () => {
            resolve();
        };

        signatureImg.src = signature;
    });

    return currentY + maxHeight + 5;
};

/**
 * Load logo image and convert to data URL
 */
export const tryLoadLogoImage = async (): Promise<{ dataUrl: string; format: string } | null> => {
    try {
        const logoUrl = '/logo.svg';

        const response = await fetch(logoUrl, {
            method: 'GET',
            headers: {
                'Accept': 'image/svg+xml',
            }
        });

        if (response.ok) {
            const svgText = await response.text();

            return new Promise((resolve) => {
                const img = new Image();
                const svgBlob = new Blob([svgText], {type: 'image/svg+xml'});
                const url = URL.createObjectURL(svgBlob);

                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        URL.revokeObjectURL(url);
                        resolve(null);
                        return;
                    }

                    canvas.width = 400;
                    canvas.height = 400;
                    ctx.drawImage(img, 0, 0, 400, 400);

                    const dataUrl = canvas.toDataURL('image/png');
                    URL.revokeObjectURL(url);

                    resolve({
                        dataUrl,
                        format: 'PNG'
                    });
                };

                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    resolve(null);
                };

                img.src = url;
            });
        }

        return null;
    } catch (error) {
        console.warn('Could not load logo:', error);
        return null;
    }
};

/**
 * Format date and time in German locale
 */
export const formatDateTime = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Format date in German locale
 */
export const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE');
};

/**
 * Generate PDF filename from task title and current date
 */
export const generateFileName = (taskTitle: string): string => {
    const today = new Date().toISOString().split('T')[0];
    const sanitizedTitle = taskTitle
        .replace(/[^a-zA-Z0-9äöüÄÖÜß\s]/g, '')
        .replace(/\s+/g, '_')
        .substring(0, 50);
    return `Rapport_${sanitizedTitle}_${today}.pdf`;
};
